import { useState, useMemo, useCallback } from "react"
import useDate from "~/ui-rtk/hooks/date"
import type { SelectedCreative } from "../types"
import type { CreativePickerItem } from "../hooks/useCreativePickerQuery"
import CreativeGrid from "./CreativeGrid"

const dayjs = useDate()

type TabMode = "ads" | "organic"

const ADS_SORT_OPTIONS = [
  { value: "brand_value_agg", label: "Brand Value" },
  { value: "spend", label: "Spend" },
  { value: "purchase_value", label: "Purchase Value" },
  { value: "purchases", label: "Purchases" },
  { value: "likes", label: "Likes" },
  { value: "shares", label: "Shares" },
  { value: "reach", label: "Reach" },
]

const ORGANIC_SORT_OPTIONS = [
  { value: "brand_value_agg", label: "Brand Value" },
  { value: "likes", label: "Likes" },
  { value: "comments", label: "Comments" },
  { value: "shares", label: "Shares" },
  { value: "saves", label: "Saves" },
  { value: "reach", label: "Reach" },
]

interface CreativeSelectionStepProps {
  selectedCreatives: SelectedCreative[]
  onSelectionChange: (creatives: SelectedCreative[]) => void
  onNext: () => void
  onBack: () => void
}

export default function CreativeSelectionStep({
  selectedCreatives,
  onSelectionChange,
  onNext,
  onBack,
}: CreativeSelectionStepProps) {
  const [activeTab, setActiveTab] = useState<TabMode>("ads")
  const [sortBy, setSortBy] = useState("brand_value_agg")
  const [dateRange, setDateRange] = useState<[Date, Date]>(() => [
    dayjs().subtract(30, "day").startOf("day").toDate(),
    dayjs().toDate(),
  ])
  const [adsPage, setAdsPage] = useState(0)
  const [organicPage, setOrganicPage] = useState(0)

  // Filter states
  const [adsChannelFilter, setAdsChannelFilter] = useState("")
  const [adsNameFilter, setAdsNameFilter] = useState("")
  const [organicChannelFilter, setOrganicChannelFilter] = useState("")
  const [organicCaptionFilter, setOrganicCaptionFilter] = useState("")

  const selectedIds = useMemo(
    () => new Set(selectedCreatives.map(c => c.id)),
    [selectedCreatives],
  )

  const adCount = useMemo(
    () => selectedCreatives.filter(c => c.type === "ad").length,
    [selectedCreatives],
  )

  const organicCount = useMemo(
    () => selectedCreatives.filter(c => c.type === "organic").length,
    [selectedCreatives],
  )

  const adsColumnFilters = useMemo(() => {
    const filters: Array<{ id: string; value: string | string[] }> = []
    if (adsChannelFilter) {
      filters.push({ id: "media_channel", value: [adsChannelFilter] })
    }
    if (adsNameFilter) {
      filters.push({ id: "root_ad_name", value: adsNameFilter })
    }
    return filters.length > 0 ? filters : undefined
  }, [adsChannelFilter, adsNameFilter])

  const organicColumnFilters = useMemo(() => {
    const filters: Array<{ id: string; value: string | string[] }> = []
    if (organicChannelFilter) {
      filters.push({ id: "media_channel", value: [organicChannelFilter] })
    }
    if (organicCaptionFilter) {
      filters.push({ id: "post_caption", value: organicCaptionFilter })
    }
    return filters.length > 0 ? filters : undefined
  }, [organicChannelFilter, organicCaptionFilter])

  const handleToggleSelect = useCallback(
    (itemId: string, item: CreativePickerItem) => {
      if (selectedIds.has(itemId)) {
        onSelectionChange(selectedCreatives.filter(c => c.id !== itemId))
      } else {
        onSelectionChange([
          ...selectedCreatives,
          {
            id: itemId,
            type: item.type,
            thumbnail:
              item.resolvedImageUrl ||
              item.resolvedThumbnailUrl ||
              item.thumbnail,
            title: item.title,
            mediaChannel: item.mediaChannel,
            metrics: {
              brandValue: item.brandValue,
              spend: item.spend,
              purchaseValue: item.purchaseValue,
              comments: item.comments,
              likes: item.likes,
              shares: item.shares,
              saves: item.saves,
              reach: item.reach,
            },
          },
        ])
      }
    },
    [selectedCreatives, selectedIds, onSelectionChange],
  )

  const sortOptions =
    activeTab === "ads" ? ADS_SORT_OPTIONS : ORGANIC_SORT_OPTIONS

  const handleDateChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    idx: 0 | 1,
  ) => {
    const newDate = new Date(e.target.value)
    if (isNaN(newDate.getTime())) return
    setDateRange(prev => {
      const next = [...prev] as [Date, Date]
      next[idx] = newDate
      return next
    })
    setAdsPage(0)
    setOrganicPage(0)
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-basic-white mb-1">
          Select Creative
        </h2>
        <p className="text-sm text-basic-light/60">
          Choose ads and organic posts to include in your campaign. The AI will
          group them into themed adsets.
        </p>
      </div>

      {/* Controls row */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Date range */}
        <div className="flex items-center gap-1.5">
          <input
            type="date"
            value={dayjs(dateRange[0]).format("YYYY-MM-DD")}
            onChange={e => handleDateChange(e, 0)}
            className="input input-bordered input-sm bg-system-gray-900/50 border-system-gray-700/50 text-basic-white text-xs"
          />
          <span className="text-xs text-basic-light/40">to</span>
          <input
            type="date"
            value={dayjs(dateRange[1]).format("YYYY-MM-DD")}
            onChange={e => handleDateChange(e, 1)}
            className="input input-bordered input-sm bg-system-gray-900/50 border-system-gray-700/50 text-basic-white text-xs"
          />
        </div>

        {/* Sort */}
        <select
          value={sortBy}
          onChange={e => {
            setSortBy(e.target.value)
            setAdsPage(0)
            setOrganicPage(0)
          }}
          className="select select-bordered select-sm py-0 bg-system-gray-900/50 border-system-gray-700/50 text-basic-white text-xs"
        >
          {sortOptions.map(opt => (
            <option key={opt.value} value={opt.value}>
              Sort: {opt.label}
            </option>
          ))}
        </select>

        {/* Filters for active tab */}
        {activeTab === "ads" ? (
          <>
            <select
              value={adsChannelFilter}
              onChange={e => {
                setAdsChannelFilter(e.target.value)
                setAdsPage(0)
              }}
              className="select select-bordered select-sm py-0 bg-system-gray-900/50 border-system-gray-700/50 text-basic-white text-xs"
            >
              <option value="">All Channels</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="tiktok">TikTok</option>
              <option value="youtube">YouTube</option>
            </select>
            <input
              type="text"
              placeholder="Filter by ad name..."
              value={adsNameFilter}
              onChange={e => {
                setAdsNameFilter(e.target.value)
                setAdsPage(0)
              }}
              className="input input-bordered input-sm bg-system-gray-900/50 border-system-gray-700/50 text-basic-white text-xs placeholder:text-basic-light/30 w-40"
            />
          </>
        ) : (
          <>
            <select
              value={organicChannelFilter}
              onChange={e => {
                setOrganicChannelFilter(e.target.value)
                setOrganicPage(0)
              }}
              className="select select-bordered select-sm py-0 bg-system-gray-900/50 border-system-gray-700/50 text-basic-white text-xs"
            >
              <option value="">All Channels</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="tiktok">TikTok</option>
              <option value="youtube">YouTube</option>
            </select>
            <input
              type="text"
              placeholder="Filter by caption..."
              value={organicCaptionFilter}
              onChange={e => {
                setOrganicCaptionFilter(e.target.value)
                setOrganicPage(0)
              }}
              className="input input-bordered input-sm bg-system-gray-900/50 border-system-gray-700/50 text-basic-white text-xs placeholder:text-basic-light/30 w-40"
            />
          </>
        )}

        {/* Total selected */}
        <div className="ml-auto">
          <span className="text-sm font-medium text-primary">
            {selectedCreatives.length} selected
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-system-gray-700/50">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "ads"
              ? "border-primary text-primary"
              : "border-transparent text-basic-light/50 hover:text-basic-light/70"
          }`}
          onClick={() => {
            setActiveTab("ads")
            setSortBy("brand_value_agg")
          }}
        >
          Ads
          {adCount > 0 && (
            <span className="ml-1.5 text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded-full">
              {adCount}
            </span>
          )}
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "organic"
              ? "border-primary text-primary"
              : "border-transparent text-basic-light/50 hover:text-basic-light/70"
          }`}
          onClick={() => {
            setActiveTab("organic")
            setSortBy("brand_value_agg")
          }}
        >
          Organic Posts
          {organicCount > 0 && (
            <span className="ml-1.5 text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded-full">
              {organicCount}
            </span>
          )}
        </button>
      </div>

      {/* Grid */}
      <div className="max-h-[60vh] overflow-y-auto">
        {activeTab === "ads" ? (
          <CreativeGrid
            mode="ads"
            dateRange={dateRange}
            sortBy={sortBy}
            sortDir="desc"
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            columnFilters={adsColumnFilters}
            page={adsPage}
            onLoadMore={() => setAdsPage(p => p + 1)}
          />
        ) : (
          <CreativeGrid
            mode="organic"
            dateRange={dateRange}
            sortBy={sortBy}
            sortDir="desc"
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            columnFilters={organicColumnFilters}
            page={organicPage}
            onLoadMore={() => setOrganicPage(p => p + 1)}
          />
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <button className="btn btn-ghost" onClick={onBack}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
            />
          </svg>
          Back
        </button>
        <button
          className="btn btn-primary"
          disabled={selectedCreatives.length === 0}
          onClick={onNext}
        >
          Generate Structures ({selectedCreatives.length} creative)
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
            />
          </svg>
        </button>
      </div>
    </div>
  )
}
